import { AxiosInstance, AxiosResponse } from "axios";

export function getSupportTickets(
	api: AxiosInstance,
	tenantId: number
): Promise<AxiosResponse> {
	return api.get(`/support/?tenantId=${tenantId}`);
}

export function getKontrolSupportTickets(
	api: AxiosInstance,
	opts?: { tenantId?: number; signal?: AbortSignal }
  ): Promise<AxiosResponse> {
	return api.get("/kontroledge/servicedesk/gettickets", {
	params: opts?.tenantId != null ? { tenantId: opts.tenantId } : undefined,
	signal: opts?.signal,
	});
  }

export function getSupportTicketById(
	api: AxiosInstance,
	ticketId: string | number
): Promise<AxiosResponse> {
	return api.get(`/support/${ticketId}`);
}

export function getSupportKontrolTicketById(
	api: AxiosInstance,
	ticketId: string | number
): Promise<AxiosResponse> {
	const encodedID = encodeURIComponent(ticketId);
	return api.get(`/kontroledge/servicedesk/getone/${encodedID}`);
}
export function postReplyTicket(
	api: AxiosInstance,
	ticketId: string | number,
	data: { comment: string; files?: File[] }
): Promise<AxiosResponse> {
	const encodedID = encodeURIComponent(ticketId);
	const formData = new FormData();
	formData.append("comment", data.comment);
	//To send attachments:
	if (data.files) {
		for (const file of data.files) {
			formData.append("files[]", file);
		}
	}

	return api.post(`/kontroledge/servicedesk/reply/${encodedID}`, formData, {
		headers: {
			"Content-Type": "multipart/form-data",
		},
	});
}

export async function downloadAttachment(
	api: AxiosInstance,
	ticketId: string | number,
	attachmentId: string | number
): Promise<Blob> {
	// Adjust the URL and query params as needed based on your backend
	// e.g. /kontroledge/servicedesk/attachmentcontent?IdTicket=xxxx&IdAttachment=xxxx

	const encodedID= encodeURIComponent(ticketId);
	const response = await api.get(
		`/kontroledge/servicedesk/attachment/${encodedID}/${attachmentId}`,
		{
			responseType: "blob",
		}
	);
	return response.data as Blob;
}

/**
 * Ajustamos la firma de la función para que reciba 'type' como un string,
 * y así poder usarlo para 'Change Request', 'Incident Report', etc.
 */
export function getListSolicitud(
	api: AxiosInstance,
	type: string, // e.g. "Change Request" o "Incident Report"
	product: string
): Promise<AxiosResponse> {
	// Codificamos el "type" por si lleva espacios, por ejemplo "Incident%20Report".
	const encodedType = encodeURIComponent(type);
	return api.get(
		`/support/list_solicitud?type=${encodedType}&product=${product}`
	);
}

/**
 * También ajustamos 'getListTwoLevel' para que reciba 'type' dinámico.
 */
export function getListTwoLevel(
	api: AxiosInstance,
	type: string,
	product: string,
	twoLevel: string
): Promise<AxiosResponse> {
	const encodedType = encodeURIComponent(type);
	const encodedTwoLevel = encodeURIComponent(twoLevel);
	return api.get(
		`/support/list_two_level?type=${encodedType}&twolevel=${encodedTwoLevel}&product=${product}`
	);
}

export function getPortsAndLag(
	api: AxiosInstance,
	tenantId: number
): Promise<AxiosResponse> {
	return api.get(
		`/tenant/${tenantId}/services?includeChildServices=true` //Check  this
	);
}

/**
 * El payload para crear el ticket es el mismo; sólo varía el campo "Tipo"
 * (por ejemplo "Incident Report" o "Change Request").
 */
export interface CreateTicketPayload {
	Tipo?: string;
	product?: string;
	cloud?: string;
	region?: string;
	TipoSolicitud?: string;
	level_3?: string;
	Resumen?: string;
	Prioridad?: number | string;
	Descripcion?: string;
	files?: File[];
	Nombre_de_contacto?: string;
	Correo?: string;
	phone_number?: string;
}

export interface CreateKontrolTicketPayload {
	requesttype: string;
	summary: string;
	description: string;
	priority?: number;
	subscriptionID?: string;
	files?: File[];
}

/**
 * Función para crear un ticket con multipart/form-data.
 */
export function createTicket(
	api: AxiosInstance,
	payload: CreateTicketPayload
): Promise<AxiosResponse> {
	const formData = new FormData();

	if (payload.Tipo) formData.append("Tipo", payload.Tipo);
	if (payload.product) formData.append("product", payload.product);
	if (payload.cloud) formData.append("cloud", payload.cloud);
	if (payload.region) formData.append("region", payload.region);
	if (payload.TipoSolicitud)
		formData.append("TipoSolicitud", payload.TipoSolicitud);
	if (payload.level_3) formData.append("level_3", payload.level_3);
	if (payload.Resumen) formData.append("Resumen", payload.Resumen);
	if (payload.Prioridad)
		formData.append("Prioridad", String(payload.Prioridad));
	if (payload.Descripcion) formData.append("Descripcion", payload.Descripcion);

	if (payload.files) {
		for (const file of payload.files) {
			formData.append("files[]", file);
		}
	}
	if (payload.Nombre_de_contacto) {
		formData.append("Nombre_de_contacto", payload.Nombre_de_contacto);
	}
	if (payload.Correo) {
		formData.append("Correo", payload.Correo);
	}
	if (payload.phone_number) {
		formData.append("phone_number", payload.phone_number);
	}

	return api.post("/support/CreatedTicket", formData, {
		headers: {
			"Content-Type": "multipart/form-data",
		},
	});
}

export function createKontrolTicket(
	api: AxiosInstance,
	payload: CreateKontrolTicketPayload
): Promise<AxiosResponse> {
	const formData = new FormData();

	if (payload.requesttype) formData.append("requesttype", payload.requesttype);
	if (payload.summary) formData.append("summary", payload.summary);
	if (payload.priority) formData.append("priority", String(payload.priority));
	if (payload.description) formData.append("description", payload.description);
	if (payload.subscriptionID) formData.append("subscriptionID", payload.subscriptionID);
	if (payload.files) {
		for (const file of payload.files) {
			formData.append("files[]", file);
		}
	}

	return api.post("/kontroledge/servicedesk/createticket", formData, {
		headers: {
			"Content-Type": "multipart/form-data",
		},
	});
}
